<template>
  <main class="p-enteraccount">
    <section class="p-enteraccount__wrap">
      <h2 class="c-regist__title">{{ t('AddTeamPage.title') }}</h2>
      <RegisterFlow :steps="6" :nowStep="4" v-if="!enterAccountStore.isInvitedUser" />
      <p class="c-regist__comment">{{ t('AddTeamPage.message') }}</p>
      <p class="p-enteraccount__add_team_link">
        {{ t('AddTeamPage.enterAccountLinkBefore') }}
        <router-link :to="{ name: 'InvitePage', query: { tokenId: enterAccountStore.tokenId } }">
          {{ t('AddTeamPage.here') }}
        </router-link>
        {{ t('AddTeamPage.enterAccountLinkAfter') }}
      </p>
      <dl class="p-enteraccount__lists">
        <div class="p-enteraccount__list">
          <dt class="p-enteraccount__list-title">
            <span class="-text">{{ t('AddTeamPage.id') }}</span>
          </dt>
          <dd>
            <input-text
              ref="inFieldUserName"
              v-model:input-value="enterAccountStore.username"
              class="p-enteraccount__list-data-item"
              name="username"
              :placeholder="t('AddTeamPage.idMsg')"
              rules="userIdFormat"
            />
          </dd>
        </div>
        <div class="p-enteraccount__list">
          <dt class="p-enteraccount__list-title">
            <span class="-text">{{ t('AddTeamPage.password') }}</span>
          </dt>
          <dd>
            <input-text
              ref="inFieldPassword"
              v-model:input-value="enterAccountStore.password"
              class="p-enteraccount__list-data-item"
              name="password"
              type="password"
              :placeholder="t('EnterAccountPage.password')"
              rules="required|passwordFormat"
            />
          </dd>
        </div>
      </dl>
      <div class="p-enteraccount__link">
        <button
          @click="onClickEnterAccount"
          :disabled="!isEnableButton"
          :class="{ isDisabled: !isEnableButton }"
          class="c-button"
        >
          <span class="c-button__text">{{ t('AddTeamPage.confirm') }}</span>
        </button>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import RegisterFlow from '@/components/registerTeam/RegisterFlow.vue'
import StoreUtil from '@/stores/StoreUtil'
import InputText from '@/components/common/field/InputText.vue'
import useMaintenanceStatus from '@/Hooks/useMaintenanceStatus'

export default defineComponent({
  name: 'AddTeamPage',
  setup() {
    const router = useRouter()
    const enterAccountStore = StoreUtil.useStore('EnterAccountStore')
    const messageDialogStore = StoreUtil.useStore('MessageDialogStore')
    const ErrorStore = StoreUtil.useStore('ErrorStore')
    const loginStore = StoreUtil.useStore('LoginStore')
    const { checkMaintenanceStatusByApiBaseUrl } = useMaintenanceStatus()
    const inFieldUserName = ref<InstanceType<typeof InputText>>()
    const inFieldPassword = ref<InstanceType<typeof InputText>>()
    const isEnableButton = computed(
      () => inFieldUserName.value?.meta.valid && inFieldPassword.value?.meta.valid,
    )
    const { t } = useI18n()

    onMounted(async () => {
      const isAvailable = await checkMaintenanceStatusByApiBaseUrl()
      if (!isAvailable) {
        router.replace({ name: 'MaintenancePage' })
        return
      }
      enterAccountStore.isInvitedUser = true
      enterAccountStore.isExistingAccount = true
      if (typeof router.currentRoute.value.query.tokenId !== 'string') {
        ErrorStore.setConfig('tokenInvalid.invite')
        router.replace({ name: 'ErrorPage' })
        return
      }
      enterAccountStore.tokenId = router.currentRoute.value.query.tokenId
      const validationResult = await enterAccountStore.validateToken()
      if (!validationResult.isSuccess || !validationResult.res.data.isValid) {
        await messageDialogStore.openWithSettings({
          title: t('EnterAccountPage.urlInvalidError.title'),
          message: t('EnterAccountPage.urlInvalidError.messageForInvite'),
          status: 'error',
        })
        ErrorStore.setConfig('tokenInvalid.invite')
        router.replace({ name: 'ErrorPage' })
      }
    })
    return {
      enterAccountStore,
      isEnableButton,
      t,
      inFieldUserName,
      inFieldPassword,
      async onClickEnterAccount() {
        const res = await enterAccountStore.fetchAccessToken()

        if (!res.isSuccess) {
          await messageDialogStore.openWithSettings({
            title: t('AddTeamPage.loginErrorDialog.title'),
            message: t('AddTeamPage.loginErrorDialog.message'),
            status: 'caution',
            cancelButton: false,
          })
          return
        }

        // メールアドレスが登録されていない場合（ LINE で登録されたアカウント）、メールアドレス登録画面に遷移する。
        if (!loginStore.mailAddress) {
          router.push({
            name: 'MailAddressRegistrationPage',
            query: { tokenId: enterAccountStore.tokenId },
          })
          return
        }

        router.replace({ name: 'InviteConfirmationPage' })
      },
    }
  },
  components: {
    RegisterFlow,
    InputText,
  },
})
</script>

<style scoped lang="scss">
.isDisabled {
  opacity: 0.3;
  pointer-events: none;
}
</style>
